import { useMutation as rqUseMutation } from "react-query";

import { AxiosResponse } from "axios";

import {
  UploadForecastRequest,
  parseAndDetectForecastProducts,
  uploadArpsWithMappings,
  uploadWellData
} from "../../../../api/userForecasts";
import { ForecastActivityType, TYPE_WELLS } from "../../../../constants";
import { UserArpsFile } from "../../../../models/UserArpsModel";
import { WellDataColumnMapping } from "../arps-upload-wizard/constants";

interface UseArpsUploadMutationsProps {
  projectId: string;
  selectedFolderId: string;
  selectedFolderName: string;
  type: ForecastActivityType;
  processedFiles: UserArpsFile[];
  onLoadingStateChange: (isLoading: boolean) => void;
  onUploadSuccess: (response: AxiosResponse, requestData: Record<string, string>) => void;
  onUploadError: (error: unknown) => void;
  resetState: () => void;
}

export const useArpsUploadMutations = ({
  projectId,
  selectedFolderId,
  selectedFolderName,
  type,
  processedFiles,
  onLoadingStateChange,
  onUploadSuccess,
  onUploadError,
  resetState
}: UseArpsUploadMutationsProps) => {
  const detectProductsMutation = rqUseMutation(async () => {
    const formData = new FormData();
    for (const file of processedFiles) {
      formData.append(file.fileType, file.fileUpload);
    }
    formData.append("source", "valnav");
    formData.append("folderName", selectedFolderName);

    return await parseAndDetectForecastProducts({
      formData,
      projectId,
      uploadType: type === TYPE_WELLS ? "type-wells" : "forecast",
      folderId: selectedFolderId,
      folderName: selectedFolderName
    } as UploadForecastRequest);
  });

  const uploadArpsMutation = rqUseMutation(
    async (params: {
      productMappings: Record<string, string>;
      deletedProducts: string[];
      primaryProduct?: string;
    }) => {
      const { productMappings, deletedProducts, primaryProduct } = params;
      const formData = new FormData();
      for (const file of processedFiles) {
        formData.append(file.fileType, file.fileUpload);
      }
      formData.append("source", "valnav");
      formData.append("folderName", selectedFolderName);
      formData.append("mappings", JSON.stringify(productMappings));
      formData.append("deletedProducts", JSON.stringify(deletedProducts));
      formData.append("uploadType", type === TYPE_WELLS ? "type-wells" : "forecast");

      if (primaryProduct) {
        formData.append("primaryProduct", primaryProduct);
      }

      return await uploadArpsWithMappings({
        formData,
        projectId
      } as UploadForecastRequest);
    },
    {
      onMutate: () => {
        onLoadingStateChange?.(true);
      },
      onSuccess: (response) => {
        onUploadSuccess?.(response, { folderId: selectedFolderId });
        onLoadingStateChange?.(false);
        resetState();
      },
      onError: (error: unknown) => {
        onLoadingStateChange?.(false);
        onUploadError?.(error);
      }
    }
  );

  /**
   * Mutation for uploading well data (normalization parameters) in bulk.
   * This is used when the file type is WellDataUpload.
   */
  const uploadWellDataMutation = rqUseMutation(
    async (params: { mappings: WellDataColumnMapping[] }) => {
      const { mappings } = params;
      const formData = new FormData();
      for (const file of processedFiles) {
        formData.append("upload", file.fileUpload);
      }
      formData.append("folderId", selectedFolderId);
      formData.append("mappings", JSON.stringify(mappings));

      return await uploadWellData({
        formData,
        projectId
      });
    },
    {
      onMutate: () => {
        onLoadingStateChange?.(true);
      },
      onSuccess: (response) => {
        onUploadSuccess?.(response as AxiosResponse, {
          folderId: selectedFolderId,
          isWellDataUpload: "true"
        });
        onLoadingStateChange?.(false);
        resetState();
      },
      onError: (error: unknown) => {
        onLoadingStateChange?.(false);
        onUploadError?.(error);
      }
    }
  );

  return {
    detectProductsMutation,
    uploadArpsMutation,
    uploadWellDataMutation
  };
};
